import { memo } from "react"
import { LandingAnimatedBrackyBreakout } from "../LandingAnimatedBrackyBreakout"
import { LandingCTA } from "../LandingCTA"
import { LandingFooter } from "../LandingFooter"
import { LandingLogoFlag } from "../LandingLogoFlag"
import { LandingMicroFooter } from "../LandingMicroFooter"
import { LandingPauseAnimationOnHidden } from "../LandingPauseAnimationOnHidden"
import { LandingSectionBracket } from "../LandingSectionBracket"
import { LandingSectionBracky } from "../LandingSectionBracky"
import { LandingSectionBrackyArena } from "../LandingSectionBrackyArena"
import { LandingSectionDemo } from "../LandingSectionDemo"
import { LandingSectionFAQ } from "../LandingSectionFAQ"
import { LandingSectionToken } from "../LandingSectionToken"
import { LandingTokenRoundel } from "../LandingTokenRoundel"

interface Props {
  revealContent: boolean
}

export const LandingMainContent = memo(function LandingMainContent({ revealContent }: Props) {
  const brackyRef = LandingPauseAnimationOnHidden<HTMLDivElement>() as React.RefObject<HTMLDivElement>
  const bracketRef = LandingPauseAnimationOnHidden<HTMLDivElement>() as React.RefObject<HTMLDivElement>
  const demoRef = LandingPauseAnimationOnHidden<HTMLDivElement>() as React.RefObject<HTMLDivElement>
  const tokenRef = LandingPauseAnimationOnHidden<HTMLDivElement>() as React.RefObject<HTMLDivElement>
  const faqRef = LandingPauseAnimationOnHidden<HTMLDivElement>() as React.RefObject<HTMLDivElement>

  return (
    <>
      {/* Rest of the landing content – fades in on md+ after small scroll. Always visible on mobile. */}
      <div
        className={`${revealContent ? "opacity-100" : "md:opacity-0 opacity-100"} md:transition-opacity md:duration-300`}
      >
        {/* @bracky Feature Section – always mounted, hidden on md+ until scroll */}
        <div
          ref={brackyRef}
          className={`w-full ${revealContent ? "md:opacity-100" : "md:opacity-0"} md:transition-opacity md:duration-300 relative z-400 translate-y-[-5.1rem] sm:translate-y-8 md:translate-y-0 mdlg:-translate-y-8 lg:-translate-y-2 xl:translate-y-[2.4rem] 2xl:translate-y-20 tall:-translate-y-40`}
        >
          <LandingSectionBracky />
          {/* Animated breakout illustration */}
          <div className="mt-8 sm:mt-14 md:mt-28 mdlg:mt-30 lg:mt-32 xl:mt-36 2xl:mt-[10.51rem]">
            <LandingAnimatedBrackyBreakout />
          </div>
          {/* Arena section with consistent top padding to account for Bracky's transforms */}
          <div className="relative pt-[-1rem] sm:pt-[-0.5rem] md:pt-0 lg:pt-2 xl:pt-4">
            <LandingSectionBrackyArena />
          </div>
        </div>

        {/* Logo Flag between Bracky and Bracket sections */}
        <div className="relative w-full   -mt-21 sm:mt-20 tall:sm:-mt-32 md:mt-12 md:-mb-8 tall:md:-mt-32 mdlg:mt-6  tall:mdlg:-mt-32 lg:mt-20 tall:lg:mt-[-6.7rem]  xl:mt-28 tall:xl:-mt-24  2xl:mt-44 tall:2xl:mt-[-4.7rem] scale-[.54] sm:scale-[.4]">
          <LandingLogoFlag className="mx-auto" />
        </div>

        <div ref={bracketRef} className="mt-[-.4rem]  sm:mt-0 lg:mt-2 xl:mt-6 2xl:mt-12">
          <LandingSectionBracket />
        </div>

        {/* Live Demo Section */}
        <div ref={demoRef} className="mb-2 md:mb-32">
          <LandingSectionDemo />
        </div>

        {/* Token Section – render immediately to prevent layout shift during FAQ jump */}
        <div ref={tokenRef} className="w-full mt-10">
          <LandingSectionToken />
        </div>

        {/* FAQ Section */}
        <div ref={faqRef} className="mt-10 mb-10">
          {/* Mount FAQ immediately so nav jump positions correctly */}
          <LandingSectionFAQ />
        </div>
      </div>

      {/* Footer */}
      <LandingFooter />

      {/* Bottom spacer so page content isn't obscured by fixed CTA on mobile */}
      <div className="h-24 md:hidden" />

      {/* Bottom CTA (renders via portal) - Mobile only */}
      <div className="md:hidden">
        <LandingCTA variant="mobile" />
      </div>

      {/* $BRACKY token roundel – portals to body, no extra classes needed */}
      <LandingTokenRoundel />
      {/* Micro footer – visible on md+ only */}
      <LandingMicroFooter />
    </>
  )
})
